import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n.jsx";
import { PageHeader } from "../components/Bits";
import TransInput from "../components/TransInput";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Building, Network, Users, Building2, Plus } from "lucide-react";

export default function Organization() {
  const { t, tn } = useI18n();
  const [tree, setTree] = useState(null);
  const [modal, setModal] = useState(null); // 'department' | 'team'
  const [name, setName] = useState({});
  const [deptId, setDeptId] = useState("");

  const load = () => api.get("/org/tree").then((r) => { setTree(r.data); if (r.data.departments[0]) setDeptId(r.data.departments[0].id); });
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (modal === "department") await api.post("/config/departments", { name, parent_id: null });
      else await api.post("/config/teams", { name, department_id: deptId });
      toast.success(t("save")); setModal(null); setName({}); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };

  if (!tree) return null;
  const teamMembers = (tid) => tree.users.filter((u) => u.team_id === tid);

  return (
    <div>
      <PageHeader title={t("organization")}
        action={<div className="flex gap-2">
          <Button variant="outline" onClick={() => setModal("department")} className="gap-1" data-testid="add-dept-btn"><Plus size={15} /> {t("department")}</Button>
          <Button onClick={() => setModal("team")} className="bg-[var(--amber)] text-white gap-1" data-testid="add-team-btn"><Plus size={15} /> {t("team")}</Button>
        </div>} />

      <div className="tactical-card p-5">
        <div className="flex items-center gap-2 font-display font-bold text-lg mb-4">
          <Building className="text-[var(--amber)]" /> {tree.company?.name}
        </div>
        <div className="space-y-4 ml-2 border-l-2 border-[var(--border)] pl-5">
          {tree.departments.map((d) => (
            <div key={d.id}>
              <div className="flex items-center gap-2 font-semibold"><Network size={16} className="text-[var(--blue)]" /> {tn(d.name)}</div>
              <div className="ml-6 mt-2 space-y-2 border-l border-[var(--border)] pl-4">
                {tree.teams.filter((tm) => tm.department_id === d.id).map((tm) => (
                  <div key={tm.id} className="tactical-card p-3">
                    <div className="flex items-center gap-2 font-semibold text-sm"><Users size={15} className="text-[var(--green)]" /> {tn(tm.name)}
                      <span className="text-xs text-[var(--text-3)]">({teamMembers(tm.id).length})</span></div>
                    <div className="ml-6 mt-1 flex flex-wrap gap-1">
                      {teamMembers(tm.id).map((u) => <span key={u.id} className="text-xs bg-[var(--bg)] px-2 py-0.5 rounded-full">{u.name}</span>)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-5">
          <div className="text-xs uppercase font-bold text-[var(--text-3)] mb-2">{t("projects")}</div>
          <div className="flex flex-wrap gap-2">
            {tree.projects.map((p) => <span key={p.id} className="tactical-card px-3 py-1.5 text-sm flex items-center gap-1"><Building2 size={13} className="text-[var(--amber)]" /> {p.name}</span>)}
          </div>
        </div>
      </div>

      <Dialog open={!!modal} onOpenChange={() => setModal(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("create")} — {modal === "department" ? t("department") : t("team")}</DialogTitle></DialogHeader>
          <TransInput value={name} onChange={setName} placeholder={t("name")} />
          {modal === "team" && (
            <select className="border rounded-md h-10 px-2 text-sm w-full mt-2" value={deptId} onChange={(e) => setDeptId(e.target.value)} data-testid="team-dept-select">
              {tree.departments.map((d) => <option key={d.id} value={d.id}>{tn(d.name)}</option>)}
            </select>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>{t("cancel")}</Button>
            <Button onClick={save} className="bg-[var(--amber)] text-white" data-testid="org-save">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
